import React, { useEffect, useState } from "react";
import { FileUpload } from "primereact/fileupload";
import { Button } from "primereact/button";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  getRawShootsByProduct,
  delRawShoot,
} from "../../store/AsyncMethods/ProductMethod";

const MAX_RAW_SHOOT_FILES = 20;

// Raw shoot: the uncompressed photoshoot images/video for a product,
// kept separate from the customer-facing product image gallery. Visible
// and manageable only from the admin panel — never shown on the storefront.
export default function RawShootUpload({ formik, productId }) {
  const dispatch = useDispatch();
  const [existingFiles, setExistingFiles] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);

  const loadExisting = async () => {
    if (!productId) return;
    setLoadingExisting(true);
    const files = await dispatch(getRawShootsByProduct(productId));
    setExistingFiles(Array.isArray(files) ? files : []);
    setLoadingExisting(false);
  };

  useEffect(() => {
    loadExisting();
  }, [productId]);

  const handleDeleteExisting = async (fileId) => {
    const ok = await dispatch(delRawShoot(fileId));
    if (ok) {
      setExistingFiles((prev) => prev.filter((f) => f.id !== fileId));
    }
  };

  const totalPendingAndExisting =
    (formik.values.rawShoot?.length || 0) + existingFiles.length;

  const emptyTemplate = () => (
    <div className="flex items-center flex-col">
      <i
        className="pi pi-camera mt-3 p-5"
        style={{
          fontSize: "2em",
          borderRadius: "50%",
          backgroundColor: "var(--surface-b)",
          color: "var(--surface-d)",
        }}
      ></i>
      <span
        style={{ fontSize: "1em", color: "var(--text-color-secondary)" }}
        className="my-2"
      >
        Drag and drop raw shoot photos/videos here
      </span>
    </div>
  );

  const itemTemplate = (file, props) => {
    const isVideo = file.type?.startsWith("video/");
    return (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center" style={{ width: "40%" }}>
          {isVideo ? (
            <video src={file.objectURL} className="w-28 h-28 object-cover" muted />
          ) : (
            <img
              alt={file.name}
              role="presentation"
              src={file.objectURL}
              className="w-28 h-28 object-cover"
            />
          )}
          <span className="flex flex-col text-left ml-3">
            {file.name}
            <small>{props.formatSize}</small>
          </span>
        </div>
        <Button
          type="button"
          icon="pi pi-times"
          rounded
          className="p-red-btn"
          onClick={() => {
            const remaining = (formik.values.rawShoot || []).filter(
              (f) => !(f.name === file.name && f.size === file.size)
            );
            formik.setFieldValue("rawShoot", remaining);
            props.onRemove(file);
          }}
        />
      </div>
    );
  };

  return (
    <div className="card shadow-md rounded-lg p-4 mt-3">
      <label className="block font-semibold mb-2">Raw Shoot Files</label>
      <p className="text-sm text-gray-500 mb-3">
        Upload the original, uncompressed photoshoot images and video clips
        for this product. These are for internal/admin reference only — they
        are never shown on the storefront. Up to {MAX_RAW_SHOOT_FILES} files,
        25MB each (images or video).
      </p>

      {existingFiles.length > 0 && (
        <div className="mb-4">
          <div className="text-sm font-medium mb-2">
            Already uploaded ({existingFiles.length})
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {existingFiles.map((f) => (
              <div
                key={f.id}
                className="relative border rounded-md p-1 flex items-center justify-center"
                style={{ height: 100 }}
              >
                {f.file_type === "video" ? (
                  <video src={f.file_path} className="w-full h-full object-cover rounded" muted />
                ) : (
                  <img
                    src={f.file_path}
                    alt="Raw shoot file"
                    className="w-full h-full object-cover rounded"
                  />
                )}
                <Button
                  type="button"
                  icon="pi pi-times"
                  rounded
                  className="p-red-btn absolute top-1 right-1"
                  style={{ width: 24, height: 24 }}
                  onClick={() => handleDeleteExisting(f.id)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {loadingExisting && (
        <div className="text-sm text-gray-500 mb-3">Loading existing files…</div>
      )}

      <FileUpload
        disabled={totalPendingAndExisting >= MAX_RAW_SHOOT_FILES}
        name="rawShoot"
        files={formik.values.rawShoot || []}
        accept="image/*,video/*"
        emptyTemplate={emptyTemplate}
        itemTemplate={itemTemplate}
        chooseOptions={{ icon: "pi pi-fw pi-upload", className: "p-primary-btn" }}
        progressBarTemplate={() => <></>}
        multiple
        pt={{ content: { className: "!p-2 !rounded-md" } }}
        onSelect={(e) => {
          const allSelectedFiles = Array.from(e.files);
          const current = formik.values.rawShoot || [];

          const newFiles = allSelectedFiles.filter(
            (file) =>
              !current.some(
                (c) => c.name === file.name && c.size === file.size
              )
          );
          if (newFiles.length === 0) return;

          if (
            existingFiles.length + current.length + newFiles.length >
            MAX_RAW_SHOOT_FILES
          ) {
            toast.error(
              `Maximum ${MAX_RAW_SHOOT_FILES} raw shoot files allowed per product.`
            );
            return;
          }

          formik.setFieldValue("rawShoot", [...current, ...newFiles]);
        }}
        onClear={() => {
          formik.setFieldValue("rawShoot", []);
        }}
        onError={() => {
          toast.error("Error selecting raw shoot file.");
        }}
      />
    </div>
  );
}
